import {
  isReportingRangePreset,
  type ReportingRangePreset
} from '../analytics/reportingRange';
import type { UserActivityPageTab, UserActivityReportMetric } from './types';

export type UserActivityTrendsSubview =
  | 'daily'
  | 'weekly'
  | 'hourOfDay'
  | 'dayOfWeek'
  | 'durationBands';

export type UserActivityAccessSubview =
  | 'lastPath'
  | 'role'
  | 'tenant'
  | 'endReason'
  | 'status';

export type UserActivityTechnologySubview =
  | 'device'
  | 'browser'
  | 'os'
  | 'viewport'
  | 'screen'
  | 'pixelRatio'
  | 'touch'
  | 'build'
  | 'theme';

export type UserActivityGeoSubview =
  | 'country'
  | 'continent'
  | 'asn'
  | 'asName'
  | 'asDomain'
  | 'timezone'
  | 'locale'
  | 'coverage';

export type UserActivityUsersMetric = UserActivityReportMetric | 'activeDays';

export interface UserActivityUiPrefs {
  tab: UserActivityPageTab;
  trendsSubview: UserActivityTrendsSubview;
  accessSubview: UserActivityAccessSubview;
  technologySubview: UserActivityTechnologySubview;
  geoSubview: UserActivityGeoSubview;
  reportMetric: UserActivityReportMetric;
  usersMetric: UserActivityUsersMetric;
  topN: number;
  rangePreset: ReportingRangePreset;
  customFrom: string | null;
  customTo: string | null;
}

const DEFAULTS: UserActivityUiPrefs = {
  tab: 'summary',
  trendsSubview: 'daily',
  accessSubview: 'lastPath',
  technologySubview: 'device',
  geoSubview: 'country',
  reportMetric: 'sessions',
  usersMetric: 'sessions',
  topN: 12,
  rangePreset: '1m',
  customFrom: null,
  customTo: null
};

function isCurrentTab(v: unknown): v is UserActivityPageTab {
  return (
    v === 'summary' ||
    v === 'trends' ||
    v === 'users' ||
    v === 'access' ||
    v === 'technology' ||
    v === 'geo' ||
    v === 'sessions'
  );
}

function migrateTab(v: unknown): UserActivityPageTab | null {
  if (v === 'weekly') return 'trends';
  if (v === 'clients') return 'technology';
  if (isCurrentTab(v)) return v;
  return null;
}

function isTrendsSubview(v: unknown): v is UserActivityTrendsSubview {
  return (
    v === 'daily' ||
    v === 'weekly' ||
    v === 'hourOfDay' ||
    v === 'dayOfWeek' ||
    v === 'durationBands'
  );
}

function isAccessSubview(v: unknown): v is UserActivityAccessSubview {
  return (
    v === 'lastPath' ||
    v === 'role' ||
    v === 'tenant' ||
    v === 'endReason' ||
    v === 'status'
  );
}

function isTechnologySubview(v: unknown): v is UserActivityTechnologySubview {
  return (
    v === 'device' ||
    v === 'browser' ||
    v === 'os' ||
    v === 'viewport' ||
    v === 'screen' ||
    v === 'pixelRatio' ||
    v === 'touch' ||
    v === 'build' ||
    v === 'theme'
  );
}

function isGeoSubview(v: unknown): v is UserActivityGeoSubview {
  return (
    v === 'country' ||
    v === 'continent' ||
    v === 'asn' ||
    v === 'asName' ||
    v === 'asDomain' ||
    v === 'timezone' ||
    v === 'locale' ||
    v === 'coverage'
  );
}

function isReportMetric(v: unknown): v is UserActivityReportMetric {
  return v === 'sessions' || v === 'uniqueUsers' || v === 'totalMinutes';
}

function isUsersMetric(v: unknown): v is UserActivityUsersMetric {
  return isReportMetric(v) || v === 'activeDays';
}

function isTopN(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 3 && v <= 50;
}

export function loadUserActivityUiPrefs(storageKey: string): UserActivityUiPrefs {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<UserActivityUiPrefs> & {
      summaryView?: unknown;
      clientsView?: unknown;
    };

    // Migrate legacy clientsView into technologySubview when present.
    let technologySubview = isTechnologySubview(parsed.technologySubview)
      ? parsed.technologySubview
      : DEFAULTS.technologySubview;
    if (
      !isTechnologySubview(parsed.technologySubview) &&
      (parsed.clientsView === 'device' ||
        parsed.clientsView === 'browser' ||
        parsed.clientsView === 'os')
    ) {
      technologySubview = parsed.clientsView;
    }

    return {
      tab: migrateTab(parsed.tab) ?? DEFAULTS.tab,
      trendsSubview: isTrendsSubview(parsed.trendsSubview)
        ? parsed.trendsSubview
        : DEFAULTS.trendsSubview,
      accessSubview: isAccessSubview(parsed.accessSubview)
        ? parsed.accessSubview
        : DEFAULTS.accessSubview,
      technologySubview,
      geoSubview: isGeoSubview(parsed.geoSubview) ? parsed.geoSubview : DEFAULTS.geoSubview,
      reportMetric: isReportMetric(parsed.reportMetric)
        ? parsed.reportMetric
        : DEFAULTS.reportMetric,
      usersMetric: isUsersMetric(parsed.usersMetric) ? parsed.usersMetric : DEFAULTS.usersMetric,
      topN: isTopN(parsed.topN) ? Math.round(parsed.topN) : DEFAULTS.topN,
      rangePreset: isReportingRangePreset(parsed.rangePreset)
        ? parsed.rangePreset
        : DEFAULTS.rangePreset,
      customFrom: typeof parsed.customFrom === 'string' ? parsed.customFrom : null,
      customTo: typeof parsed.customTo === 'string' ? parsed.customTo : null
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveUserActivityUiPrefs(storageKey: string, prefs: UserActivityUiPrefs): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch {
    // ignore quota / private mode
  }
}

export function isUserActivityPageTab(v: unknown): v is UserActivityPageTab {
  return normalizeUserActivityPageTab(v) !== null;
}

/** Normalize URL/deep-link tab values (including legacy names) to current tabs. */
export function normalizeUserActivityPageTab(v: unknown): UserActivityPageTab | null {
  return migrateTab(v);
}
